<template>
  <article
    class="transition-colors p-2 gap-y-2 flex flex-col [&>*]:flex [&>*]:flex-col"
    :class="{ 'hover:bg-gray-50': !loading, '[&>*]:grow': !compact }"
  >
    <Shimmer :loading="loading">
      <component
        :is="loading ? 'div' : RouterLink"
        :aria-label="ariaLabel"
        class="flex flex-col h-full"
        :to="loading ? undefined : to"
      >
        <slot name="media" />

        <CarvisAvatar
          v-if="avatarImageId !== undefined || avatarFallback"
          :avatar-image-id="avatarImageId ?? null"
          class="w-2/5 max-w-24 mx-auto mt-2 flex-none!"
          :fallback="avatarFallback"
        />

        <p
          aria-label="title"
          class="items-center font-semibold text-md text-blue flex mt-1"
          :class="{ 'justify-center': align === 'center' }"
        >
          <CarvisFlash
            v-if="isUnread"
            class="mr-0.5"
            :enabled="true"
          />
          <span class="w-fit truncate">
            {{ title }}
          </span>
        </p>

        <div :class="{ 'text-center': align === 'center', '[&>*:first-child]:justify-between': compact, 'grow': !compact }">
          <slot />
        </div>

        <div
          v-if="chips?.length"
          class="flex flex-col gap-y-1.5 px-[1px] mt-2 grow-0!"
        >
          <CarvisChip
            v-for="(chip, index) in chips"
            :key="index"
            class="w-full"
            :href="chip.href"
            :label="chip.label"
          >
            <template #icon>
              <component
                :is="chip.icon"
                :size="16"
              />
            </template>
            {{ chip.text }}
          </CarvisChip>
        </div>

        <p
          v-if="timestamp"
          class="text-xs flex items-center gap-x-1 text-gray-400 grow-0! mt-1"
        >
          <Calendar
            class="flex shrink-0"
            :size="14"
          />
          <span>
            {{ md && timestamp.prefix && compact ? `${timestamp.prefix} ` : '' }}{{ formatDistance(timestamp.date) }}
          </span>
        </p>
      </component>
    </Shimmer>
  </article>
</template>

<script setup lang="ts">
import { Shimmer } from '@shimmer-from-structure/vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import Calendar from 'vue-material-design-icons/Calendar.vue'
import { RouterLink } from 'vue-router'

import type { AvatarFallback } from '@/components/generic/CarvisAvatar.vue'
import { formatDistance } from '@/utils'

const { md } = useBreakpoints(breakpointsTailwind)

defineProps<{
  loading: boolean
  to: string
  ariaLabel?: string
  avatarImageId?: string | null
  avatarFallback?: AvatarFallback
  title: string
  align?: 'left' | 'center'
  compact?: boolean
  isUnread?: boolean
  chips?: Chip[]
  timestamp?: { date: string, prefix?: string }
}>()
</script>
